import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { message } from 'antd';

import DialogSoftwareBulkTags from '@totem/components/software/software_report/dialogSoftwareBulkTags';
import DialogSoftwareWhereFound from '@totem/components/software/software_report/dialogSoftwareWhereFound';
import MergeDialog from '@totem/components/software/software_report/mergeDialog';
import SoftwareContext from '@totem/components/software/software_report/softwareContext';
import {
  SoftwareCatalogInput,
  SoftwarePackage,
} from '@totem/components/software/software_report/types';
import { emptyId } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';
import { omitNilOrEmpty } from '@totem/utilities/objectUtilities';

type Props = {
  children?: ReactNode;
  endPoint?: string;
  organizationId?: string;
  defaultPublisher?: string;
};

const SoftwareContainer = ({
  children,
  endPoint,
  organizationId,
  defaultPublisher,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [messageApi, contextHolder] = message.useMessage();
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [monitoredOnly, setMonitoredOnly] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<SoftwarePackage[]>([]);
  const [rawData, setRawData] = useState<SoftwarePackage[]>([]);
  const [action, setAction] = useState<string>('');
  const [actionItem, setActionItem] = useState<SoftwarePackage>(null);
  const [selectedPackages, setSelectedPackages] = useState<SoftwarePackage[]>(
    [],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page')) || 1;

  const [input, updateInput] = useState<SoftwareCatalogInput>({
    pageSize: 20,
    page,
    sortField: 'name',
    sortDirection: '1',
    name: '',
    publisher: isNotNull(defaultPublisher) ? defaultPublisher : '',
    tag: '',
  });

  const filterData = (swData: SoftwarePackage[]) => {
    let filtered = [...swData];
    let clearSelections = false;

    if (typeof input.name !== 'undefined' && input.name !== '') {
      filtered = filtered.filter((chk) => {
        const searchTerms = input.name.toLowerCase().split('|');
        for (let idx = 0; idx < searchTerms.length; idx++) {
          if (
            chk.name.toLowerCase().startsWith(searchTerms[idx].toLowerCase())
          ) {
            return true;
          }
        }
        return chk.name.toLowerCase().startsWith(input.name.toLowerCase());
      });

      clearSelections = true;
    }
    if (typeof input.publisher !== 'undefined' && input.publisher !== '') {
      filtered = filtered.filter((chk) =>
        chk.publisher.toLowerCase().startsWith(input.publisher.toLowerCase()),
      );

      clearSelections = true;
    }
    if (typeof input.tag !== 'undefined' && input.tag !== '') {
      filtered = filtered.filter((chk) => {
        if (isNotNull(chk.tags) && chk.tags.length > 0) {
          for (let idx = 0; idx < chk.tags.length; idx++) {
            if (
              chk.tags[idx].toLowerCase().startsWith(input.tag.toLowerCase())
            ) {
              return true;
            }
          }
        }
        return false;
      });

      clearSelections = true;
    }

    if (clearSelections) {
      setSelectedPackages([]);
    }

    setData(filtered);
  };

  const setPackageSelected = (
    softwarePackage: SoftwarePackage,
    selected: boolean,
  ) => {
    if (selected) {
      if (
        selectedPackages.findIndex((chk) => chk.id === softwarePackage.id) <= -1
      ) {
        setSelectedPackages([...selectedPackages, softwarePackage]);
      }
    } else {
      const filtered = selectedPackages.filter(
        (chk) => chk.id !== softwarePackage.id,
      );
      setSelectedPackages(filtered);
    }
  };

  const setInput = (updated: Partial<SoftwareCatalogInput>) => {
    updateInput((prevInput) => omitNilOrEmpty({ ...prevInput, ...updated }));
    if (isNotNull(updated.page) && updated.page !== page) {
      searchParams.set('page', updated.page.toString());

      setSearchParams(searchParams);
    }
    //setRefreshData(true);
  };

  useEffect(() => {
    filterData(rawData);
  }, [input]);

  useEffect(() => {
    setRefreshData(true);
  }, [monitoredOnly, userProfile.organization.id]);

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);

      setIsLoading(true);

      const uri = isNotNull(endPoint)
        ? endPoint
        : monitoredOnly
          ? `${DEVICES_ENDPOINT}/software/catalog/monitored`
          : `${DEVICES_ENDPOINT}/software/catalog`;

      fetch(uri, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => (res.ok ? res.json() : []))
        .then((result: SoftwarePackage[]) => {
          const packages = Array.isArray(result) ? result : [];

          setRawData(packages);

          filterData(packages);
        })
        .then(() => {
          setIsLoading(false);
        })
        .catch(() => {
          setRawData([]);

          filterData([]);

          setIsLoading(false);
        });
    }
  }, [refreshData, getApiToken]);

  const handleAction = (newAction: string, item: SoftwarePackage) => {
    switch (newAction) {
      case 'merge':
        if (selectedPackages.length > 1) {
          setAction(newAction);

          setActionItem(item);
        } else {
          messageApi.error('Select two or more packages before merging!');
        }
        break;
      case 'bulk-tag':
        if (selectedPackages.length > 0) {
          setAction(newAction);

          setActionItem(item);
        } else {
          messageApi.error('Select two or more packages before tagging!');
        }
        break;
      case 'where-found':
        if (selectedPackages.length > 0) {
          setAction(newAction);

          setActionItem(item);
        } else {
          messageApi.error('Select one or more packages first!');
        }
        break;
      case 'clear_package_selections':
        setSelectedPackages([]);
        break;
      case 'refresh':
        setRefreshData(true);

        setAction('');

        setActionItem(null);
        break;
      default:
        setAction(newAction);

        setActionItem(item);
    }
  };

  return (
    <SoftwareContext.Provider
      value={{
        loading: isLoading,
        organizationId: isNotNull(organizationId) ? organizationId : emptyId,
        monitoredOnly,
        setMonitoredOnly,
        data,
        rawData,
        action,
        actionItem,
        onAction: handleAction,
        input,
        setInput,
        selectedPackages,
        setPackageSelected,
      }}
    >
      {contextHolder}
      <div>{children}</div>
      {action === 'merge' && <MergeDialog />}
      {action === 'bulk-tag' && (
        <DialogSoftwareBulkTags open={action === 'bulk-tag'} />
      )}
      {action === 'where-found' && (
        <DialogSoftwareWhereFound open={action === 'where-found'} />
      )}
    </SoftwareContext.Provider>
  );
};

export default SoftwareContainer;
